import { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Mail,
  MessageSquare,
  ChevronDown,
  ChevronUp,
  ExternalLink,
  Paperclip,
  Clock,
  User,
  Send,
  RefreshCw,
  Plus,
  Link2,
  AlertCircle,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GMAIL_ACCOUNTS = [
  "info@alpinearmour.com.au",
  "scott@scottlistondesign.com.au"
];

function formatDateTime(dateStr) {
  if (!dateStr) return "";
  const date = new Date(dateStr);
  return date.toLocaleDateString("en-AU", {
    day: "numeric",
    month: "short",
    hour: "numeric",
    minute: "2-digit",
  });
}

function formatRelativeTime(dateStr) {
  if (!dateStr) return "";
  const date = new Date(dateStr);
  const now = new Date();
  const diff = now - date;
  const hours = Math.floor(diff / (1000 * 60 * 60));
  const days = Math.floor(hours / 24);
  
  if (hours < 1) return "Just now";
  if (hours < 24) return `${hours}h ago`;
  if (days < 7) return `${days}d ago`;
  return formatDateTime(dateStr);
}

function extractName(fromHeader) {
  const match = fromHeader?.match(/^([^<]+)/);
  if (match) {
    return match[1].trim().replace(/"/g, "");
  }
  return fromHeader?.replace(/<.*>/, "").trim() || "";
}

function extractEmail(fromHeader) {
  const match = fromHeader?.match(/<([^>]+)>/);
  return match ? match[1].toLowerCase() : fromHeader?.toLowerCase().trim() || "";
}

function isAwaitingReply(threads) {
  if (!threads?.length) return false;
  // Check most recent thread's most recent message
  const lastThread = threads.reduce((latest, t) => {
    if (!latest) return t;
    return new Date(t.last_date) > new Date(latest.last_date) ? t : latest;
  }, null);
  
  if (!lastThread?.messages?.length) return false;
  const lastMsg = lastThread.messages[lastThread.messages.length - 1];
  const lastSender = extractEmail(lastMsg.from);
  return !GMAIL_ACCOUNTS.includes(lastSender);
}

// ==================== SEND EMAIL MODAL ====================

function SendEmailModal({ open, onClose, jobId, jobName, customerEmail, accounts, onSent }) {
  const [to, setTo] = useState(customerEmail || "");
  const [subject, setSubject] = useState(jobName ? `Re: ${jobName}` : "");
  const [body, setBody] = useState("");
  const [from, setFrom] = useState(GMAIL_ACCOUNTS[0]);
  const [sending, setSending] = useState(false);
  
  useEffect(() => {
    if (open) {
      setTo(customerEmail || "");
      setSubject(jobName ? `Re: ${jobName}` : "");
      setBody("");
      const connected = accounts?.find(a => a.connected);
      if (connected) setFrom(connected.email);
    }
  }, [open, customerEmail, jobName, accounts]);
  
  const handleSend = async () => {
    if (!to.trim() || !subject.trim()) {
      toast.error("Please fill in recipient and subject");
      return;
    }
    
    setSending(true);
    try {
      const token = localStorage.getItem("auth_token");
      
      await axios.post(`${API}/gmail/send`, {
        to,
        subject,
        body_html: body.replace(/\n/g, "<br>"),
        from_account: from,
        job_id: jobId,
      }, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      
      toast.success("Email sent!");
      onClose();
      onSent?.();
    } catch (error) {
      console.error("Failed to send:", error);
      toast.error(error.response?.data?.detail || "Failed to send email");
    } finally {
      setSending(false);
    }
  };
  
  const connectedAccounts = accounts?.filter(a => a.connected) || [];
  
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Send Email</DialogTitle>
          <DialogDescription>
            Send an email and automatically link it to this job.
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-3 py-2">
          <div className="flex items-center gap-3">
            <label className="w-16 text-sm text-gray-500 shrink-0">From:</label>
            <Select value={from} onValueChange={setFrom}>
              <SelectTrigger className="flex-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {connectedAccounts.map(acc => (
                  <SelectItem key={acc.email} value={acc.email}>{acc.email}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          <div className="flex items-center gap-3">
            <label className="w-16 text-sm text-gray-500 shrink-0">To:</label>
            <input
              type="text"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              className="flex-1 px-3 py-2 border border-gray-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-aa-primary/30"
              placeholder="recipient@example.com"
            />
          </div>
          
          <div className="flex items-center gap-3">
            <label className="w-16 text-sm text-gray-500 shrink-0">Subject:</label>
            <input
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              className="flex-1 px-3 py-2 border border-gray-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-aa-primary/30"
            />
          </div>
          
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            rows={10}
            className="w-full px-3 py-2 border border-gray-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-aa-primary/30 resize-none"
            placeholder="Write your message..."
          />
          
          <p className="text-xs text-gray-400">
            Signature will be added automatically. Email will be linked to this job.
          </p>
        </div>
        
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSend} disabled={sending} className="bg-aa-primary">
            <Send className="w-4 h-4 mr-2" />
            {sending ? "Sending..." : "Send"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ThreadSummary({ thread, expanded, onToggle }) {
  const latestMsg = thread.messages?.[thread.messages.length - 1];
  const hasAttachments = thread.messages?.some(m => m.attachments?.length > 0);
  
  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden">
      {/* Header - always visible */}
      <div
        className="flex items-center gap-3 px-4 py-3 bg-white hover:bg-gray-50 cursor-pointer"
        onClick={onToggle}
      >
        <div className="p-2 rounded-full bg-aa-primary/10">
          <Mail className="w-4 h-4 text-aa-primary" />
        </div>
        
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-medium text-sm text-gray-900 truncate">
              {thread.subject}
            </span>
            {hasAttachments && (
              <Paperclip className="w-3.5 h-3.5 text-gray-400 shrink-0" />
            )}
          </div>
          <p className="text-xs text-gray-500 truncate">
            {thread.message_count} message{thread.message_count !== 1 ? "s" : ""} • 
            Last from {extractName(latestMsg?.from)}
          </p>
        </div>
        
        <div className="flex items-center gap-2 shrink-0">
          <Badge variant="secondary" className="text-[10px]">
            {thread.account_email?.split("@")[0]}
          </Badge>
          <span className="text-xs text-gray-400">
            {formatDateTime(thread.last_date)}
          </span>
          {expanded ? (
            <ChevronUp className="w-4 h-4 text-gray-400" />
          ) : (
            <ChevronDown className="w-4 h-4 text-gray-400" />
          )}
        </div>
      </div>
      
      {/* Expanded content */}
      {expanded && (
        <div className="border-t border-gray-100 bg-gray-50 p-4">
          {/* Message list */}
          <div className="space-y-3 mb-4">
            {thread.messages?.map((msg, idx) => (
              <div key={msg.id} className="flex gap-3">
                <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center shrink-0 text-xs font-medium text-gray-600">
                  {extractName(msg.from).charAt(0).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-gray-800">
                      {extractName(msg.from)}
                    </span>
                    <span className="text-xs text-gray-400">
                      {formatDateTime(msg.date)}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600 line-clamp-2 mt-0.5">
                    {msg.snippet || msg.body_plain?.substring(0, 150)}
                  </p>
                </div>
              </div>
            ))}
          </div>
          
          {/* Open thread link */}
          <div className="flex justify-end">
            {thread.gmail_url ? (
              <a
                href={thread.gmail_url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-sm text-aa-primary hover:underline"
                data-testid={`open-in-gmail-${thread.id}`}
              >
                Open in Gmail
                <ExternalLink className="w-3.5 h-3.5" />
              </a>
            ) : (
              <Link
                to={`/inbox/thread/${thread.id}?account=${encodeURIComponent(thread.account_email)}`}
                className="inline-flex items-center gap-1 text-sm text-aa-primary hover:underline"
              >
                Open full thread
                <ExternalLink className="w-3.5 h-3.5" />
              </Link>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default function JobCommunicationTimeline({ jobId, jobName, customerEmail }) {
  const navigate = useNavigate();
  const [threads, setThreads] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedThreads, setExpandedThreads] = useState({});
  const [showSendEmail, setShowSendEmail] = useState(false);
  
  const getToken = () => localStorage.getItem("auth_token");
  
  const loadAccounts = useCallback(async () => {
    try {
      const token = getToken();
      const res = await axios.get(`${API}/gmail/accounts`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      setAccounts(res.data.accounts || []);
    } catch (error) {
      console.error("Failed to load accounts:", error);
    }
  }, []);
  
  const loadThreads = useCallback(async () => {
    if (!jobId) return;
    
    setLoading(true);
    try {
      const token = getToken();
      const res = await axios.get(`${API}/jobs/${jobId}/emails`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      setThreads(res.data.threads || []);
    } catch (error) {
      console.error("Failed to load email threads:", error);
    } finally {
      setLoading(false);
    }
  }, [jobId]);
  
  useEffect(() => {
    loadAccounts();
    loadThreads();
  }, [loadAccounts, loadThreads]);
  
  const toggleThread = (threadId) => {
    setExpandedThreads(prev => ({
      ...prev,
      [threadId]: !prev[threadId]
    }));
  };
  
  // Computed values for summary
  const totalMessages = threads.reduce((sum, t) => sum + (t.message_count || 0), 0);
  const lastDate = threads.length > 0 
    ? threads.reduce((latest, t) => {
        const tDate = new Date(t.last_date);
        return tDate > latest ? tDate : latest;
      }, new Date(0))
    : null;
  const awaiting = isAwaitingReply(threads);
  const hasConnectedAccount = accounts.some(a => a.connected);
  
  if (loading) {
    return (
      <div className="bg-aa-card rounded-lg shadow-sm p-6">
        <div className="flex items-center gap-2 mb-4">
          <MessageSquare className="w-5 h-5 text-aa-primary" />
          <h3 className="font-semibold text-gray-800">Communication</h3>
        </div>
        <div className="flex items-center justify-center py-8">
          <RefreshCw className="w-5 h-5 text-gray-400 animate-spin" />
        </div>
      </div>
    );
  }
  
  return (
    <div className="bg-aa-card rounded-lg shadow-sm p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <MessageSquare className="w-5 h-5 text-aa-primary" />
          <h3 className="font-semibold text-gray-800">Communication</h3>
        </div>
        
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={loadThreads}>
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </div>
      
      {/* Summary Bar */}
      {threads.length > 0 && (
        <div className="flex items-center gap-4 px-4 py-3 bg-gray-50 rounded-lg mb-4">
          <div className="flex items-center gap-2">
            <Mail className="w-4 h-4 text-gray-500" />
            <span className="text-sm text-gray-700">
              <span className="font-medium">{threads.length}</span> thread{threads.length !== 1 ? "s" : ""}
              {totalMessages > 0 && (
                <span className="text-gray-500"> • {totalMessages} message{totalMessages !== 1 ? "s" : ""}</span>
              )}
            </span>
          </div>
          
          {lastDate && (
            <div className="flex items-center gap-1.5 text-sm text-gray-500">
              <Clock className="w-3.5 h-3.5" />
              <span>Last: {formatRelativeTime(lastDate.toISOString())}</span>
            </div>
          )}
          
          {awaiting && (
            <Badge className="bg-amber-100 text-amber-700 text-xs">
              <AlertCircle className="w-3 h-3 mr-1" />
              Awaiting Reply
            </Badge>
          )}
        </div>
      )}
      
      {/* Action Buttons */}
      <div className="flex items-center gap-2 mb-4">
        {hasConnectedAccount ? (
          <Button 
            size="sm" 
            className="bg-aa-primary"
            onClick={() => setShowSendEmail(true)}
          >
            <Send className="w-4 h-4 mr-2" />
            Send Email
          </Button>
        ) : (
          <Link to="/inbox">
            <Button size="sm" variant="outline">
              <Mail className="w-4 h-4 mr-2" />
              Connect Gmail
            </Button>
          </Link>
        )}
        
        <Link to="/inbox">
          <Button size="sm" variant="outline">
            <Link2 className="w-4 h-4 mr-2" />
            Link Existing Email
          </Button>
        </Link>
      </div>
      
      {/* Thread List */}
      {threads.length === 0 ? (
        <div className="text-center py-8 text-gray-500 border border-dashed border-gray-200 rounded-lg">
          <Mail className="w-10 h-10 mx-auto mb-3 text-gray-300" />
          <p className="text-sm">No emails linked to this job yet</p>
          <p className="text-xs text-gray-400 mt-1">
            Link emails from the inbox to see them here
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {threads.map(thread => (
            <ThreadSummary
              key={thread.id}
              thread={thread}
              expanded={expandedThreads[thread.id]}
              onToggle={() => toggleThread(thread.id)}
            />
          ))}
        </div>
      )}
      
      {/* Send Email Modal */}
      <SendEmailModal
        open={showSendEmail}
        onClose={() => setShowSendEmail(false)}
        jobId={jobId}
        jobName={jobName}
        customerEmail={customerEmail}
        accounts={accounts}
        onSent={loadThreads}
      />
    </div>
  );
}
